import * as z from "zod";
import { userFacingError } from "../authorization";
import { mutator } from "./mutator";

// Keys are deprecated in favor of service accounts; the ones that remain must
// not be immortal. New keys are capped at this TTL. `expiresAt` is optional in
// the schema for back-compat with stale clients that don't send it -- the
// handler always sets one, defaulting to the cap.
export const MAX_API_KEY_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export const createApiKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  start: z.string(),
  key: z.string(),
  spaceId: z.string().nullable(),
  // When set, the key authenticates AS this service account: it carries no
  // permissions of its own, deriving authority from the account's grants.
  // Mutually exclusive with spaceId.
  serviceAccountId: z.string().nullable().optional(),
  expiresAt: z.number().optional(),
});

export const createApiKey = mutator({
  redactKeys: ["key"],
  schema: createApiKeySchema,
  resolve: {
    space: {
      kind: "space",
      from: "spaceId",
      optional: true,
    },
    serviceAccount: {
      kind: "serviceAccount",
      from: "serviceAccountId",
      optional: true,
    },
  },
  permissions: [
    {
      permission: "service_account:manage",
      scopeId: ({ args }) => args.serviceAccountId ?? null,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: { id, name, start, spaceId, serviceAccountId, expiresAt },
  }) => {
    if (spaceId && serviceAccountId) {
      throw userFacingError(
        "A key cannot be bound to both a space and a service account"
      );
    }

    // User and space keys (no service account) are the long-lived keys we
    // retired; the server refuses to mint new ones so a stale client or direct
    // mutation can't bypass it. Service-account-bound keys are the supported
    // path.
    if (!serviceAccountId) {
      throw userFacingError(
        "User API keys are no longer available. Use a service account instead."
      );
    }

    const { userId } = ctx;
    const now = Date.now();

    // Cap the lifetime: a requested expiry is honored only if sooner than the
    // max; otherwise (or if unset) the key expires at the cap.
    const cap = now + MAX_API_KEY_TTL_MS;
    const effectiveExpiry =
      expiresAt != null && expiresAt < cap ? expiresAt : cap;

    await tx.mutate.apikey.insert({
      id,
      name,
      start,
      referenceId: userId,
      spaceId,
      serviceAccountId: serviceAccountId ?? null,
      enabled: true,
      expiresAt: effectiveExpiry,
      createdAt: now,
      updatedAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
